import { Image, Pagination, Table, type TableColumnProps } from "zhihao-ui-react";
import type {
    MonitorSortOrder,
    TransportTrackMonitorPage,
    TransportTrackMonitorVo
} from "@/api/transportTrackMonitorTypes";
import { formatCaptureTime, getCaptureCategoryOption, getCaptureLocation, getCaptureReasons } from "./presentation";
import { NOT_DATA_IMAGE } from "./notDataImage";

interface CaptureTableProps {
    page: TransportTrackMonitorPage;
    sortOrder: MonitorSortOrder;
    onToggleSort: () => void;
    onPageChange: (page: number) => void;
    onViewOnMap: (record: TransportTrackMonitorVo) => void;
}

/** 旧版 CaptureTable 的 React 等价组件。 */
export function CaptureTable({ page, sortOrder, onToggleSort, onPageChange, onViewOnMap }: CaptureTableProps) {
    const columns: TableColumnProps<TransportTrackMonitorVo>[] = [
        {
            label: "序号",
            key: "index",
            width: 115,
            render: ({ rowIndex }) => (page.currentPage - 1) * page.pageSize + rowIndex + 1
        },
        {
            label: "照片",
            key: "photo",
            width: 148,
            render: ({ row }) =>
                row.captureFile?.path ? (
                    <Image className="capture-table__photo" width={120} height={64} src={row.captureFile.path} />
                ) : (
                    <span className="capture-table__photo-fallback">加载失败</span>
                )
        },
        {
            label: "抓拍时间",
            key: "captureTime",
            width: 200,
            render: ({ row }) => formatCaptureTime(row.displayTime || row.captureTime)
        },
        { label: "运单状态", key: "waybillState", width: 160, render: ({ row }) => row.orderState?.name || "--" },
        { label: "货仓状态", key: "cargoState", width: 142, render: ({ row }) => row.warehouseState?.name || "--" },
        {
            label: "抓拍分类",
            key: "category",
            width: 162,
            render: ({ row }) => {
                const category = getCaptureCategoryOption(row);
                return (
                    <span
                        className="capture-category-tag"
                        style={{ color: category.color, backgroundColor: `${category.color}1a` }}
                    >
                        {category.label}
                    </span>
                );
            }
        },
        { label: "抓拍原因", key: "reasons", width: 160, render: ({ row }) => getCaptureReasons(row) },
        { label: "AIS位置", key: "aisLocation", minWidth: 300, render: ({ row }) => getCaptureLocation(row) },
        {
            label: "操作",
            key: "operation",
            fixed: "right",
            width: 96,
            render: ({ row }) =>
                Number.isFinite(row.longitude) && Number.isFinite(row.latitude) ? (
                    <button type="button" className="capture-table__map-link" onClick={() => onViewOnMap(row)}>
                        在地图查看
                    </button>
                ) : (
                    "--"
                )
        }
    ];

    return (
        <div className="capture-table" data-testid="in-transit-capture-table">
            <div className="capture-table__header">
                <div className="capture-table__title">
                    <span>抓拍列表</span>
                    <small>
                        共<strong>{page.total}</strong>张抓拍
                    </small>
                </div>
                <button
                    type="button"
                    className="capture-table__sort"
                    aria-label={sortOrder === "DESC" ? "抓拍时间倒序" : "抓拍时间正序"}
                    onClick={onToggleSort}
                >
                    时间排序
                    <svg className="capture-table__sort-icon" viewBox="0 0 16 16" aria-hidden="true">
                        <path
                            className={sortOrder === "ASC" ? "is-active" : ""}
                            d="M7.612 2.233a.5.5 0 0 1 .776 0l2.762 3.39a.5.5 0 0 1-.388.816H5.238a.5.5 0 0 1-.388-.816l2.762-3.39Z"
                        />
                        <path
                            className={sortOrder === "DESC" ? "is-active" : ""}
                            d="M7.612 13.767a.5.5 0 0 0 .776 0l2.762-3.39a.5.5 0 0 0-.388-.816H5.238a.5.5 0 0 0-.388.816l2.762 3.39Z"
                        />
                    </svg>
                </button>
            </div>
            <Table
                rowKey={row => String(row.id)}
                columns={columns}
                data={page.list}
                pagination={false}
                emptyText={null}
            />
            {!page.list.length ? (
                <div className="capture-table__empty">
                    <img src={NOT_DATA_IMAGE} alt="" />
                    <span>本运单暂无抓拍记录</span>
                </div>
            ) : null}
            <Pagination
                current={page.currentPage}
                pageSize={page.pageSize}
                total={page.total}
                onChange={onPageChange}
                showSizeChanger={false}
            />
        </div>
    );
}
